/**
 * src/lib/track.js — đo lường hành vi ẩn danh, CHỈ CHẠY KHI NGƯỜI DÙNG ĐỒNG Ý.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * Ba đích nhận cùng một sự kiện, cùng một bộ lọc dữ liệu:
 *   1. Google Analytics 4 (qua Firebase)  — báo cáo, phễu, khám phá đường dẫn
 *   2. Microsoft Clarity                  — gắn nhãn phiên ghi hình để lọc
 *   3. Nhật ký nội bộ trên Firestore      — dashboard /admin/thong-ke
 *
 * Điều kiện để bất kỳ thứ gì được gửi đi:
 *   - .env có VITE_ANALYTICS=on và VITE_FIREBASE_MEASUREMENT_ID (GA4), và/hoặc
 *     VITE_CLARITY_ID (Clarity);
 *   - người dùng đã bấm "Đồng ý" trên banner (src/lib/consent.js);
 *   - không phải trang /admin, và trình duyệt không bật "không đếm".
 *
 * KHÔNG BAO GIỜ gửi số liệu người dùng nhập (tiền, tên công ty, email…). Chỉ
 * nhận tham số trong danh sách THAM_SO (src/lib/statsModel.js), chuỗi ≤ 60 ký
 * tự, số nhỏ. Đường dẫn trang bỏ phần ?query và #neo.
 * ─────────────────────────────────────────────────────────────────────────
 */
import { layApp, CO_CAU_HINH } from './firebase.js';
import { daDongY, boQuaTrinhDuyetNay, theoDoiDongY, xoaCookieDoLuong } from './consent.js';
import { SU_KIEN, lamSachThamSo, lamSachTrang } from './statsModel.js';
import { taiClarity, suKienClarity, dungClarity, CLARITY_BAT } from './clarity.js';
import { ghiNhatKy, NHAT_KY_BAT } from './nhatKy.js';

const ENV = import.meta.env || {};
const MA_DO = ENV.VITE_FIREBASE_MEASUREMENT_ID;
export const GA_BAT = CO_CAU_HINH && Boolean(MA_DO) && ENV.VITE_ANALYTICS === 'on';
/** Có ít nhất một công cụ đo lường được cấu hình → cần hỏi ý kiến người dùng. */
export const DO_LUONG_BAT = GA_BAT || CLARITY_BAT;

const TEN_HOP_LE = new Set(SU_KIEN);

let analyticsPromise = null;
function layAnalytics() {
  if (!GA_BAT) return Promise.resolve(null);
  if (!analyticsPromise) {
    if (typeof window !== 'undefined') window[`ga-disable-${MA_DO}`] = false;
    analyticsPromise = Promise.all([layApp(), import('firebase/analytics')])
      .then(async ([app, sdk]) => {
        if (!app || !(await sdk.isSupported())) return null;
        const a = sdk.initializeAnalytics(app, {
          config: {
            // Lượt xem trang gửi tay ở ghiLuotXemTrang(), để tiêu đề trang đúng
            // và không đếm đôi. Nhớ tắt "Page changes based on browser history
            // events" trong GA4 (xem ANALYTICS.md).
            send_page_view: false,
            allow_google_signals: false,
            allow_ad_personalization_signals: false,
          },
        });
        return { a, sdk };
      })
      .catch(() => null);
  }
  return analyticsPromise;
}

function duocGhi() {
  if (!DO_LUONG_BAT || typeof window === 'undefined') return false;
  if (!daDongY() || boQuaTrinhDuyetNay()) return false;
  return !window.location.pathname.startsWith('/admin');
}

export function track(name, params) {
  if (!TEN_HOP_LE.has(name) || name === 'page_view' || !duocGhi()) return;
  const thamSo = lamSachThamSo(params);
  layAnalytics().then((x) => { if (x) x.sdk.logEvent(x.a, name, thamSo); });
  suKienClarity(name);
  ghiNhatKy(name, window.location.pathname, thamSo);
}

/** Chỉ ghi một lần mỗi phiên cho cùng khoá — tránh đếm lặp khi gõ từng ký tự. */
const daGhi = new Set();
export function trackOnce(key, name, params) {
  if (daGhi.has(key)) return;
  if (!duocGhi()) return; // chưa đồng ý thì chưa "dùng" khoá, để đồng ý xong vẫn ghi được
  daGhi.add(key);
  track(name, params);
}

/** Gọi mỗi lần đổi route. Chờ một nhịp để tiêu đề trang kịp cập nhật. */
export function ghiLuotXemTrang(path) {
  if (!duocGhi()) return;
  const trang = lamSachTrang(path);
  setTimeout(() => {
    if (lamSachTrang(window.location.pathname) !== trang) return; // người dùng đã chuyển trang tiếp
    layAnalytics().then((x) => {
      if (x) x.sdk.logEvent(x.a, 'page_view', {
        page_path: trang,
        page_location: window.location.origin + trang,
        page_title: document.title.slice(0, 100),
      });
    });
    ghiNhatKy('page_view', trang, {});
  }, 600);
}

/**
 * Ghi lúc tab bị ẩn (chuyển tab, thu nhỏ, đóng). Chỉ vào nhật ký nội bộ, để
 * dashboard tính được thời lượng của phiên chỉ xem một trang. Cách nhau ít
 * nhất 15 giây để người hay đổi tab không sinh hàng trăm bản ghi.
 */
let lanRoiTruoc = 0;
function ghiRoiTrang() {
  if (document.visibilityState !== 'hidden' || !duocGhi()) return;
  const bayGio = Date.now();
  if (bayGio - lanRoiTruoc < 15e3) return;
  lanRoiTruoc = bayGio;
  ghiNhatKy('roi_trang', window.location.pathname, {});
}

let daKhoiDong = false;
/**
 * Gọi một lần khi ứng dụng chạy trên trình duyệt. Nếu đã có đồng ý thì tải
 * công cụ đo lường; nếu chưa thì chờ người dùng chọn trên banner.
 */
export function khoiDongDoLuong() {
  if (daKhoiDong || typeof window === 'undefined' || !DO_LUONG_BAT) return;
  daKhoiDong = true;
  document.addEventListener('visibilitychange', ghiRoiTrang);
  const bat = () => {
    if (boQuaTrinhDuyetNay()) return;
    layAnalytics().then((x) => { if (x) x.sdk.setAnalyticsCollectionEnabled(x.a, true); });
    taiClarity();
  };
  if (daDongY()) bat();
  theoDoiDongY(({ chon, truoc }) => {
    if (chon === 'dong-y' && truoc !== 'dong-y') {
      bat();
      ghiLuotXemTrang(window.location.pathname); // trang người dùng đang đứng
    }
    if (chon === 'tu-choi' && truoc === 'dong-y') {
      if (MA_DO) window[`ga-disable-${MA_DO}`] = true;
      layAnalytics().then((x) => { if (x) x.sdk.setAnalyticsCollectionEnabled(x.a, false); });
      dungClarity();
      xoaCookieDoLuong();
    }
  });
}

/** Cho dashboard: công cụ nào đang được cấu hình. */
export const CAU_HINH_DO_LUONG = { ga: GA_BAT, clarity: CLARITY_BAT, nhatKy: NHAT_KY_BAT, maDo: MA_DO || '' };
